/**
 * @fileoverview X/Twitter integration content for the Integrations page.
 * @module x-twitter-connect/components/XTwitterIntegrationContent
 */

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { useTheme } from '@mui/material/styles';
import { useXTwitterConnect } from '../hooks/useXTwitterConnect';

export interface XTwitterIntegrationContentProps {
  projectId: string;
  showHeader?: boolean;
}

function formatDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function XTwitterIntegrationContent({
  projectId,
  showHeader = false,
}: XTwitterIntegrationContentProps) {
  const theme = useTheme();
  const {
    status,
    isConnected,
    loading,
    actionLoading,
    error,
    syncPreview,
    connect,
    disconnect,
    refreshStatus,
    previewOwnPostsSync,
    syncOwnPosts,
  } = useXTwitterConnect(projectId);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const connectedAt = formatDate(status?.connectedAt);
  const expiresAt = formatDate(status?.accessTokenExpiresAt);
  const lastSyncAt = formatDate(status?.lastOwnPostsSyncAt);

  return (
    <Box>
      {showHeader && (
        <>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            X / Twitter Integration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect an X account for official API access.
          </Typography>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isConnected ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          {status?.needsReconnect && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Reconnect required</AlertTitle>
              The stored X authorization can no longer be refreshed.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <TwitterIcon color="action" sx={{ fontSize: 40 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Connected as @{status?.username}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {status?.displayName || status?.xUserId || 'X account connected'}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                {(status?.scopes || []).map((scope) => (
                  <Chip key={scope} size="small" label={scope} variant="outlined" />
                ))}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshStatus}
                disabled={actionLoading}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <LinkOffIcon />}
                onClick={disconnect}
                disabled={actionLoading}
              >
                Disconnect
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
            {connectedAt && (
              <Typography variant="body2" color="text.secondary">
                Connected: {connectedAt}
              </Typography>
            )}
            {expiresAt && (
              <Typography variant="body2" color="text.secondary">
                Access token expires: {expiresAt}
              </Typography>
            )}
            {lastSyncAt && (
              <Typography variant="body2" color="text.secondary">
                Last own-post sync: {lastSyncAt} ({status?.lastOwnPostsSyncCount ?? 0} posts)
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
          {!status?.budgetPolicyConfigured ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Paid X operations are locked until an explicit project budget policy is configured.
            </Alert>
          ) : (
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">X API budget and local usage</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Today $${(status.usage?.utcDayUsd ?? 0).toFixed(4)}`} />
                <Chip size="small" label={`Month $${(status.usage?.billingMonthUsd ?? 0).toFixed(4)}`} />
                <Chip size="small" variant="outlined" label={`Per operation ≤ $${status.budgetPolicy?.maxPerOperationUsd ?? 0}`} />
                <Chip size="small" color={status.budgetPolicy?.publishingEnabled ? 'success' : 'default'} label={`Publishing ${status.budgetPolicy?.publishingEnabled ? 'enabled' : 'disabled'}`} />
              </Stack>
            </Stack>
          )}

          {syncPreview && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={(
                <Button color="inherit" size="small" onClick={syncOwnPosts} disabled={actionLoading}>
                  Sync now
                </Button>
              )}
            >
              Up to 100 posts / 1 page. Estimated maximum cost: ${Number(syncPreview.estimatedMaximumCostUsd ?? 0).toFixed(4)}. No X request was made for this preview.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={previewOwnPostsSync}
              disabled={actionLoading || status?.needsReconnect || !status?.budgetPolicyConfigured}
            >
              Preview own-post sync
            </Button>
            <Button
              variant="outlined"
              startIcon={<ForumOutlinedIcon />}
              href={`/project/${projectId}/content-plan?cpTab=engagement`}
            >
              Open Engagement
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            borderColor: theme.palette.divider,
            borderStyle: 'dashed',
          }}
        >
          <TwitterIcon color="action" sx={{ fontSize: 56, mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Connect X / Twitter
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}
          >
            Authorize Segmently with tweet read/write access. Tokens are stored in Google Secret Manager.
          </Typography>
          <Button
            variant="contained"
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <TwitterIcon />}
            onClick={connect}
            disabled={actionLoading}
          >
            {actionLoading ? 'Connecting...' : 'Connect X Account'}
          </Button>
          <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
            <AlertTitle>Required X app setup</AlertTitle>
            <Typography variant="body2">
              Configure the X / Twitter provider credentials in Admin before connecting.
            </Typography>
          </Alert>
        </Paper>
      )}
    </Box>
  );
}

export default XTwitterIntegrationContent;
